import { inject, Injectable, signal } from '@angular/core';
import {
  CreateRechercheGQL,
  CreateRechercheInput,
  DeleteRechercheGQL,
  FiltreSettingInput,
  RechercheForConsultantGQL,
  RechercheSauvegardeFragmentFragment,
  SortEnumType,
  UpdateRechercheGQL,
  UpdateRechercheInput,
} from '@lib/models/generated/graphql';
import { QueryManagerService } from 'finzzle-angular-utils';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { BehaviorSubject } from 'rxjs';
import { FiltreLine } from './filtre-lines/filtre-line.model';
@Injectable({
  providedIn: 'root',
})
export class RechercheSauvegardeService {
  private queryManagerService = inject(QueryManagerService);
  private rechercheForConsultant = inject(RechercheForConsultantGQL);
  private createRechercheGQL = inject(CreateRechercheGQL);
  private deleteRechercheGQL = inject(DeleteRechercheGQL);
  private updateRechercheGQL = inject(UpdateRechercheGQL);
  public allRecherchesSubject = new BehaviorSubject<RechercheSauvegardeFragmentFragment[]>([]);
  public recherches$ = this.allRecherchesSubject.asObservable();
  public isfetching = signal<boolean>(false);
  public isCreating = signal<boolean>(false);
  private readonly snackbarService = inject(OuiSnackbarService);
  constructor() {
    this.getConsultantRecherches();
  }
  public async getConsultantRecherches(): Promise<void> {
    this.isfetching.set(true);
    const order = { dateModification: SortEnumType.Desc };
    const result = await this.queryManagerService.handleQueryErrorsAsync(
      this.rechercheForConsultant.fetch({
        fetchPolicy: 'network-only',
        variables: { order },
      })
    );
    const data = result.data!.rechercheForConsultant;
    this.allRecherchesSubject.next(data);
    this.isfetching.set(false);
  }
  public async createRecherche(nom: string, lines: FiltreLine[]) {
    this.isCreating.set(true);
    lines = lines.filter(line => line.value !== null && !!line.builder && !!line.operator);
    const input: CreateRechercheInput = {
      label: nom || `Recherche_${this.randomLabel(15)}`,
      filtres: [...lines.map(l => this.mapLinesToInput(l))].filter(f => !!f),
    };
    const result = await this.queryManagerService.handleMutationErrorsAsync(
      this.createRechercheGQL.mutate({
        variables: { rechercheInput: input },
      })
    );
    if (result.data!.createRecherche?.id) {
      this.isCreating.set(false);
      this.snackbarService.open('Recherche sauvegardée avec succès', 'success', 5000);
    }
    this.getConsultantRecherches();
  }
  private randomLabel(length: number): string {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }
  private mapLinesToInput(line: FiltreLine): FiltreSettingInput | null {
    if (line.builderCode && line.operator && line.value !== null) {
      return {
        builderCode: line.builderCode,
        operator: line.operator,
        value: JSON.stringify(line.value),
      };
    }
    return null;
  }
  public async deleteRecherche(id: number) {
    await this.queryManagerService.handleMutationErrorsAsync(
      this.deleteRechercheGQL.mutate({
        variables: { id },
      })
    );
    this.getConsultantRecherches();
  }
  public async updateRecherche(id: number, nom: string, lines: FiltreLine[]) {
    const input: UpdateRechercheInput = {
      rechercheId: id,
      label: nom,
      filtres: [...lines.map(l => this.mapLinesToInput(l))].filter(f => !!f),
    };
    const result = await this.queryManagerService.handleMutationErrorsAsync(
      this.updateRechercheGQL.mutate({
        variables: { rechercheInput: input },
      })
    );
    this.snackbarService.open('Recherche mise à jour avec succès', 'success', 5000);
    if (result.data!.updateRecherche?.id) {
      this.isCreating.set(false);
    }
    this.getConsultantRecherches();
  }
}
